import React, {useMemo} from "react";
import moment from "moment";
import { DateFormats } from "../../../components/constants/securspace-constants";
import { PersonAndBoxMessage } from "../../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../../hooks/request/useGetBoundReportPaginated";
import { useGetBoundReportSummary } from "../../../hooks/request/useGetBoundReportSummary";
import { BoundTablePagination } from "../../../components/common/inputs/boundInputs";
import { AdminPayoutsVO } from "../../../types/model/valueObject/payout/AdminPayoutsVO";
import { AdminPayoutsSummary, adminPayoutsSummaryMapping } from "../../../types/model/valueObject/payout/AdminPayoutsSummary";
import { AdminPayoutsSortFilter, AdminPayoutsSearchParams } from "../../../components/admin/payouts/AdminPayoutsSortFilter";
import { AdminPayoutsCardListWithLoader } from "../../../components/admin/payouts/AdminPayoutsCardList";
import { adminPayoutsReportEndpoints as reportEndpoints } from "../../../requests/api-endpoints";
import { useReportDataSummaryCollapseByIndex}  from "../../../hooks/report/useReportDataSummaryCollapseByIndex";
import { useBoundQueryDownloadUrl } from "../../../hooks/report/useBoundQueryDownloadUrl";
import DownloadCSVButton from "../../../components/report/export/DownloadCSVButton";
import {
  DownloadButtonsContainer,
  ReportDataSummary,
  ReportSummaryWithLoader
} from "../../../components/report/redesigned";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: AdminPayoutsSearchParams = {
  supplierAccountId: '',
  locationId: '',
  startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
  endDate: moment().format(DateFormats.DAY),
  status: '',
  sortBy: "CREATED_ON",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const AdminPayoutsReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error
  } =  useGetBoundReportPaginated<AdminPayoutsVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: reportQuery
  });

  const {
    reportSummary,
    isReportSummaryLoading,
    reportSummaryError
  } = useGetBoundReportSummary<AdminPayoutsSummary>({
    requestUrl: reportEndpoints.summary,
    requestParams: reportQuery
  });

  const {
    collapseByIndex: expandByIndex, // TODO yf when redesigns slow down, rename this
    handleToggleCollapse: handleToggleExpand, // TODO yf same as above
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });
  useSnackbarDisplayOnError({error: reportSummaryError, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: AdminPayoutsSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  const downloadUrl = useBoundQueryDownloadUrl({ endpoint: reportEndpoints.report, query: reportQuery });

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Payouts</SecurSpaceReportTitle>
        <AdminPayoutsSortFilter
          query={reportQuery}
          onChange={handleFilterChange}
        />
        <ReportSummaryWithLoader
          visible={!shouldShowPersonAndBox}
          isLoading={isReportSummaryLoading}
          mapping={adminPayoutsSummaryMapping}
          summary={reportSummary}
        />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={pageInfo?.count || 0}
          title={"All"}
          isShowCollapseButton={true}
          hasExpandedItems={hasExpandedItems}
          setExpandedItems={setExpandedItems}
          setExpandedAll={setExpandedAll}
          handleExpandAll={handleExpandAll}
          downloadButtonsSlot={
            <DownloadButtonsContainer>
              <DownloadCSVButton title='Download Report' reportUrl={downloadUrl} />
            </DownloadButtonsContainer>
          }
        />
        <AdminPayoutsCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
          expandByIndex={expandByIndex}
          onToggleExpand={handleToggleExpand}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<AdminPayoutsSearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

export default AdminPayoutsReport;
